/**
 * 의약품 정보 생성·수정 모달.
 * 레거시 `DrugInfoCreateFromSettlementModal.js` 포팅.
 */
import type { DrugManualDetail } from '@/entities/drug-data/api/drug-data.api';
import {
  useInsertDrugsManual,
  useUpdateDrugDataDrug,
} from '@/entities/drug-data/hooks/useDrugData';
import { buildLeftPanelRows, resolveMappingFormDisplay } from '@/pages/drugs/drugCodeCopyPayload';
import {
  buildInitialCreateFormValues,
  mapDrugCreateFormToInsertItem,
  mapDrugCreateFormToPatchRequest,
  type DrugCreateFormValues,
  type SettlementRow,
} from '@/pages/drugs/drugCreateFormUtils';
import {
  ADMINISTRATION_ROUTE_TYPE_OPTIONS,
  DATA_SOURCE_TYPE_OPTIONS,
  ETC_OTC_TYPE_OPTIONS,
  REFERENCE_TAB_KEYS,
  REIMBURSEMENT_TYPE_OPTIONS,
  type ReferenceTabKey,
} from '@/pages/drugs/drugFormOptions';
import { MonthPicker } from '@/shared/components/DatePicker';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { cn } from '@/shared/lib/utils';
import { handleApiError, useToast } from '@/shared/utils/toast';
import React, { useEffect, useMemo, useState } from 'react';
import { FooterRow } from '@/shared/components/ui/row';

const ReadonlyField: React.FC<{
  label: string;
  value: string;
  multiline?: boolean;
}> = ({ label, value, multiline }) => (
  <div className="flex items-start justify-between gap-4">
    <span className="shrink-0 text-sm font-medium">{label}</span>
    {multiline ? (
      <textarea
        readOnly
        rows={2}
        className="w-56 rounded-md border border-input bg-muted/40 px-2 py-1 text-sm"
        value={value}
      />
    ) : (
      <Input readOnly value={value} className="w-56 bg-muted/40" />
    )}
  </div>
);

export interface DrugInfoCreateFromSettlementModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  refYm: string;
  settlementRow?: SettlementRow | null;
  referenceRow?: Record<string, unknown> | null;
  copyFlowTab?: ReferenceTabKey | null;
  isEdit?: boolean;
  editInitialDetail?: DrugManualDetail | null;
  editDrugCode?: string | null;
}

export const DrugInfoCreateFromSettlementModal: React.FC<
  DrugInfoCreateFromSettlementModalProps
> = ({
  open,
  onCancel,
  onSuccess,
  refYm,
  settlementRow = null,
  referenceRow = null,
  copyFlowTab = null,
  isEdit = false,
  editInitialDetail = null,
  editDrugCode = null,
}) => {
  const toast = useToast();
  const insertDrugs = useInsertDrugsManual();
  const updateDrug = useUpdateDrugDataDrug();
  const [form, setForm] = useState<DrugCreateFormValues>(() =>
    buildInitialCreateFormValues({ refYm }),
  );
  // 모달을 연 시점의 폼 값. 사용자가 지운 필드를 골라 "" 로 보내기 위해 둔다.
  const [initialForm, setInitialForm] = useState<DrugCreateFormValues>(() =>
    buildInitialCreateFormValues({ refYm }),
  );

  const isStandaloneInsert = !settlementRow;
  const showLeftPanel = Boolean(settlementRow);
  const saving = insertDrugs.isPending || updateDrug.isPending;

  const leftPanelRows = useMemo(() => {
    if (!settlementRow) return [];
    return buildLeftPanelRows(refYm, settlementRow, referenceRow, copyFlowTab);
  }, [copyFlowTab, refYm, settlementRow, referenceRow]);

  const specPlaceholder = useMemo(() => {
    if (copyFlowTab === REFERENCE_TAB_KEYS.LICENSE && referenceRow?.packUnit) {
      return `PACK_UNIT 참고: ${referenceRow.packUnit}`;
    }
    if (
      copyFlowTab === REFERENCE_TAB_KEYS.MAPPING &&
      referenceRow?.ingredientAmount
    ) {
      return `함량 참고: ${referenceRow.ingredientAmount}`;
    }
    return '(입력)';
  }, [copyFlowTab, referenceRow]);

  const unitPlaceholder = useMemo(() => {
    if (copyFlowTab === REFERENCE_TAB_KEYS.LICENSE && referenceRow?.packUnit) {
      return `PACK_UNIT 참고: ${referenceRow.packUnit}`;
    }
    if (copyFlowTab === REFERENCE_TAB_KEYS.MAPPING && referenceRow) {
      const formHint = resolveMappingFormDisplay(referenceRow);
      if (formHint) {
        return `제형·포장 참고: ${formHint}`;
      }
    }
    return '(입력)';
  }, [copyFlowTab, referenceRow]);

  useEffect(() => {
    if (!open) return;
    const initialValues = buildInitialCreateFormValues({
      refYm,
      settlementRow,
      referenceRow,
      copyFlowTab,
      isEdit,
      editInitialDetail,
    });
    setForm(initialValues);
    setInitialForm(initialValues);
  }, [
    open,
    refYm,
    settlementRow,
    referenceRow,
    copyFlowTab,
    isEdit,
    editInitialDetail,
  ]);

  const updateField = <K extends keyof DrugCreateFormValues>(
    key: K,
    value: DrugCreateFormValues[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      if (isEdit) {
        const {
          refYm: patchRefYm,
          drugCode,
          body,
        } = mapDrugCreateFormToPatchRequest(form, initialForm);
        const patchDrugCode = editDrugCode ?? drugCode;
        if (!patchRefYm || !patchDrugCode) {
          toast.error('기준월 또는 의약품 코드 정보가 없습니다.');
          return;
        }
        if (Object.keys(body).length === 0) {
          toast.error('변경할 항목이 없습니다.');
          return;
        }
        await updateDrug.mutateAsync({
          refYm: patchRefYm,
          drugCode: patchDrugCode,
          body,
        });
        toast.success('의약품 정보가 수정되었습니다.');
      } else {
        // 그 달에 같은 drugCode 행이 있으면 POST 도 서버에서 수정 분기로 가므로,
        // 미리 채워진 값을 사용자가 지운 필드는 수정 모달과 같이 "" 로 보내 비운다.
        const item = mapDrugCreateFormToInsertItem(form, initialForm);
        if (!item.refYm || !item.drugCode || !item.drugName) {
          toast.error('기준년월, 의약품 코드, 의약품명은 필수입니다.');
          return;
        }
        await insertDrugs.mutateAsync([item]);
        toast.success('의약품 정보가 등록되었습니다.');
      }
      onSuccess?.();
      onCancel();
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  if (!isStandaloneInsert && !settlementRow) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent
        className={cn(
          'max-h-[90vh] overflow-y-auto',
          isStandaloneInsert ? 'max-w-lg' : 'max-w-5xl',
        )}
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? '의약품 정보 수정' : '의약품 정보 생성'}
          </DialogTitle>
        </DialogHeader>

        <div
          className={cn(
            'mt-2 grid gap-6',
            showLeftPanel ? 'lg:grid-cols-2' : 'grid-cols-1',
          )}
        >
          {showLeftPanel ? (
            <div className="rounded-2xl border bg-muted/30 p-6">
              <h3 className="mb-4 text-lg font-semibold">기초 정보</h3>
              <div className="space-y-3">
                {leftPanelRows.map((row) => (
                  <ReadonlyField
                    key={row.label}
                    label={row.label}
                    value={row.value}
                    multiline={row.multiline}
                  />
                ))}
              </div>
            </div>
          ) : null}

          <div className="rounded-2xl border p-6">
            <h3 className="mb-4 text-lg font-semibold">
              {isEdit ? '의약품 수정 정보' : '의약품 생성 정보'}
            </h3>
            <div className="space-y-4">
              <div className="space-y-1">
                <Label>기준 년 월 (yyyyMM)</Label>
                <MonthPicker
                  placeholder="년월 선택"
                  value={form.createRefYm}
                  onChange={(v) => updateField('createRefYm', v)}
                />
              </div>
              <div className="space-y-1">
                <Label>의약품 명</Label>
                <Input
                  value={form.createDrugName}
                  onChange={(e) => updateField('createDrugName', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label>의약품 코드</Label>
                <Input
                  value={form.createDrugCode}
                  onChange={(e) => updateField('createDrugCode', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label>제약사 약품 코드</Label>
                <Input
                  value={form.createPharmaDrugCode}
                  onChange={(e) =>
                    updateField('createPharmaDrugCode', e.target.value)
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>품목 기준 코드</Label>
                <Input
                  value={form.itemStdCode}
                  onChange={(e) => updateField('itemStdCode', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label>규격</Label>
                <Input
                  placeholder={specPlaceholder}
                  value={form.spec}
                  onChange={(e) => updateField('spec', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label>단위</Label>
                <Input
                  placeholder={unitPlaceholder}
                  value={form.unit}
                  onChange={(e) => updateField('unit', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label>급여상태</Label>
                <Select
                  value={form.reimbursementType}
                  onValueChange={(v) => updateField('reimbursementType', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="(입력)" />
                  </SelectTrigger>
                  <SelectContent>
                    {REIMBURSEMENT_TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>전문/일반 구분</Label>
                <Select
                  value={form.etcOtcType}
                  onValueChange={(v) => updateField('etcOtcType', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="(입력)" />
                  </SelectTrigger>
                  <SelectContent>
                    {ETC_OTC_TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>정보구분</Label>
                <Select
                  value={form.dataSourceType}
                  onValueChange={(v) => updateField('dataSourceType', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATA_SOURCE_TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>투여경로</Label>
                <Select
                  value={form.administrationRouteType || 'none'}
                  onValueChange={(v) =>
                    updateField(
                      'administrationRouteType',
                      v === 'none' ? '' : v,
                    )
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="내복 / 주사 / 외용 / 기타 / 경구" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">(선택 안 함)</SelectItem>
                    {ADMINISTRATION_ROUTE_TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>ATC 코드</Label>
                <Input
                  value={form.atcCode}
                  onChange={(e) => updateField('atcCode', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <FooterRow justify="center" gap={12} className="mt-6">
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            취소
          </Button>
          <Button onClick={() => void handleSave()} disabled={saving}>
            {saving ? '저장 중...' : isEdit ? '수정' : '등록'}
          </Button>
        </FooterRow>
      </DialogContent>
    </Dialog>
  );
};
